// The 1-2-1's page worked out from its rows (data.js) and the app's clock: the next session, the
// sessions with their recordings and notes, the actions, the files and the Calendly link. Pure: no
// network, no React, only format.js and vimeo.js, so a test can import it as it is. Every date and
// time is UK time (format.js).
import * as f from '../../lib/format.js'
import { staffFirstName } from '../../lib/names.js'
import { parseVimeo } from '../../free/vimeo.js'

const MIN = 60000
export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// A session's own title, else "Session 3"
export const sessionTitle = s => (s?.title && s.title.trim()) || `Session ${s?.number}`

// The client's first name, for Adam's view and the files' "From Laura"; for the database's "there" (a name that gave
// none) or a bare title, the full name, never "there" (lib/names.js)
export function firstName(person) {
  if (!person) return ''
  return staffFirstName(person)
}

// "Laura Grant, since Thu 3 Sep"
export function eyebrowParts(person, space) {
  return { name: person?.full_name || firstName(person), since: space?.opened_at ? f.shortDate(space.opened_at) : '' }
}

// Book a session (CLAUDE.md, decision 33): the space's booking link with the client's name and email
// as the name and email parameters Calendly fills in, each URL-encoded (a space is %20, @ is %40).
// A link that already has a query keeps it, with any name or email of its own replaced; a #part
// stays at the end.
export function bookingLink(url, name, email) {
  if (typeof url !== 'string' || !/^https:\/\//i.test(url.trim())) return null
  const s = url.trim()
  const hashAt = s.indexOf('#')
  const hash = hashAt >= 0 ? s.slice(hashAt) : ''
  const noHash = hashAt >= 0 ? s.slice(0, hashAt) : s
  const qAt = noHash.indexOf('?')
  const base = qAt >= 0 ? noHash.slice(0, qAt) : noHash
  const keyOf = part => {
    const k = part.split('=')[0]
    try { return decodeURIComponent(k.replace(/\+/g, ' ')).toLowerCase() } catch { return k.toLowerCase() }
  }
  const kept = qAt >= 0 ? noHash.slice(qAt + 1).split('&').filter(p => p && !['name', 'email'].includes(keyOf(p))) : []
  const add = []
  if (name && String(name).trim()) add.push(`name=${encodeURIComponent(String(name).trim())}`)
  if (email && String(email).trim()) add.push(`email=${encodeURIComponent(String(email).trim())}`)
  const q = [...kept, ...add]
  return base + (q.length ? `?${q.join('&')}` : '') + hash
}

// A Zoom link that can be opened: a whole https:// address (the Library's rule)
export const zoomLink = url => (typeof url === 'string' && /^https:\/\//.test(url) ? url : null)

// The soonest session still to come or on now, with when Join opens (the product's
// join_opens_minutes before it starts, 15 by default), or null
export function nextSession(sessions, nowMs, joinMinutes = 15) {
  const list = (sessions || [])
    .filter(s => s.starts_at)
    .map(s => {
      const start = Date.parse(s.starts_at)
      return { session: s, start, end: start + (s.duration_minutes || 60) * MIN, joinAt: start - (joinMinutes ?? 15) * MIN }
    })
    .filter(x => x.end > nowMs)
    .sort((a, b) => a.start - b.start)
  const n = list[0]
  if (!n) return null
  const zoom = zoomLink(n.session.zoom_join_url)
  return {
    ...n,
    title: sessionTitle(n.session),
    when: `${f.longDate(n.start)}, ${f.time(n.start)}`,
    zoom,
    live: n.start <= nowMs,
    joinOpen: nowMs >= n.joinAt,
    joinTime: f.time(n.joinAt),
  }
}

// The sessions the client can watch and read: those whose notes Adam has shown (row level security
// gives a client only those), newest first
export function shownSessions(sessions, notes) {
  const byId = new Map((notes || []).filter(n => n.shown_at).map(n => [n.session_id, n]))
  return (sessions || [])
    .filter(s => byId.has(s.id))
    .sort((a, b) => b.number - a.number)
    .map(s => {
      const note = byId.get(s.id)
      return {
        id: s.id,
        number: s.number,
        title: sessionTitle(s),
        block: s.starts_at ? f.dateBlock(s.starts_at) : null,
        date: s.starts_at ? f.longDate(s.starts_at) : null,
        length: note.duration_seconds ? f.duration(note.duration_seconds) : null,
        duration: note.duration_seconds || 0,
        notes: note.notes && note.notes.trim() ? note.notes.trim() : null,
        recordingUrl: note.recording_url || null,
        playable: Boolean(parseVimeo(note.recording_url)),
      }
    })
}

// Where the client is in a session's recording, as every other recording says it: "Watched" once the
// hub has marked it (at the 1-2-1's watched share, 90%), "Carry on from 18:35" past the first 10
// seconds, else nothing. progress: their hub.client_recording_progress row, or null.
export function whereLine(progress) {
  if (!progress) return null
  if (progress.watched_at) return { watched: true, at: null }
  const pos = progress.position_seconds || 0
  return pos > 10 ? { watched: false, at: f.clock(pos) } : null
}

// Adam's "After session 3" box: the first session, by number, whose notes he hasn't shown yet
export function afterTarget(sessions, notes) {
  const shown = new Set((notes || []).filter(n => n.shown_at).map(n => n.session_id))
  return [...(sessions || [])].sort((a, b) => a.number - b.number).find(s => !shown.has(s.id)) || null
}

// The session a new action is agreed at (the last that has started) and the session it's for (the
// one after it): "Add an action for session 3" on the board day
export function actionTarget(sessions, nowMs) {
  const ran = (sessions || []).filter(s => s.starts_at && Date.parse(s.starts_at) <= nowMs)
    .sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at))[0] || null
  const next = nextSession(sessions, nowMs)
  const forNumber = ran ? ran.number + 1 : next ? next.session.number : 1
  return { agreedSessionId: ran?.id || null, forNumber }
}

// The actions in their order, how many are done, and the session they were agreed at (the latest)
export function actionsView(actions, sessions) {
  const items = [...(actions || [])].sort((a, b) => (a.sort_order - b.sort_order)
    || String(a.created_at || '').localeCompare(String(b.created_at || '')) || String(a.id).localeCompare(String(b.id)))
  const done = items.filter(a => a.done_at).length
  const agreedIds = new Set(items.map(a => a.agreed_session_id).filter(Boolean))
  const agreed = (sessions || []).filter(s => agreedIds.has(s.id)).sort((a, b) => b.number - a.number)[0] || null
  return {
    items,
    done,
    total: items.length,
    agreed: agreed ? { number: agreed.number, date: agreed.starts_at ? f.shortDate(agreed.starts_at) : null } : null,
    nextSort: items.reduce((m, a) => Math.max(m, a.sort_order || 0), 0) + 1,
  }
}

// "From Laura, Mon 21 Sep", "From Adam, today"
export function fileWho(file, clientFirst) {
  return file.direction === 'from_adam' ? 'Adam' : (clientFirst || 'your client')
}
export function fileWhen(file, nowMs) {
  if (!file.created_at) return null
  return f.daysFrom(nowMs, file.created_at) === 0 ? 'today' : f.shortDate(file.created_at)
}

// What Adam pastes for a recording: a Vimeo link, or Vimeo's embed code with the link in its src.
// Gives the link as the database takes it (^https://(player.)?vimeo.com/), or null when there's no
// Vimeo video in it.
export function vimeoLinkFrom(input) {
  const s = String(input || '').trim().replace(/&amp;/g, '&')
  if (!s) return null
  const src = s.match(/\bsrc\s*=\s*["']([^"']+)["']/i)
  const candidate = (src ? src[1] : s).trim()
  const m = candidate.match(/^https:\/\/(?:www\.)?((?:player\.)?vimeo\.com\/[^\s"'<>]+)/i)
  if (!m) return null
  const link = `https://${m[1]}`
  return parseVimeo(link) ? link : null
}

// A date and time typed in UK time ('2026-10-15', '19:00') as an instant, or null
export function ukToIso(date, time) {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date || ''))
  const t = /^(\d{1,2}):(\d{2})$/.exec(String(time || ''))
  if (!d || !t) return null
  const want = Date.UTC(+d[1], +d[2] - 1, +d[3], +t[1], +t[2])
  if (Number.isNaN(want) || +t[1] > 23 || +t[2] > 59) return null
  let guess = want
  // UK time is UTC or 1 hour ahead: 2 steps settle it, either side of a clock change
  for (let i = 0; i < 2; i++) {
    const u = f.uk(guess)
    guess += want - Date.UTC(u.y, u.m - 1, u.d, u.hh, u.mm)
  }
  return new Date(guess).toISOString()
}

// An instant as the date and time boxes show it, in UK time
export function ukParts(iso) {
  if (!iso) return { date: '', time: '' }
  const u = f.uk(iso)
  const p = n => String(n).padStart(2, '0')
  return { date: `${u.y}-${p(u.m)}-${p(u.d)}`, time: `${p(u.hh)}:${p(u.mm)}` }
}

// The lengths Adam can pick for a session (client_sessions.duration_minutes is 15 to 480)
export const LENGTHS = [30, 45, 60, 75, 90, 120]
export const lengthLabel = minutes => f.duration(minutes * 60)

// The file kinds sign-upload takes (client-files bucket, docs/data-model.md 11), by type and by
// ending for a browser that doesn't say a file's type
export const MAX_BYTES = 26214400
const TYPES = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
}
export const ACCEPT = Object.keys(TYPES).map(e => `.${e}`).join(',')
export function fileType(name, type) {
  const given = String(type || '').trim().toLowerCase()
  if (Object.values(TYPES).includes(given)) return given
  const ext = String(name || '').toLowerCase().split('.').pop()
  return TYPES[ext] || given
}
export const typeAllowed = type => Object.values(TYPES).includes(type)

// The address a question from the space links to, which opens the space for the client and, at
// 2-step login, Adam's view of it (/mentorship?space=<id>)
export function spacePath(product, spaceId) {
  return `${product?.path || '/mentorship'}?space=${spaceId}`
}
